<script setup>
import { ref, computed } from 'vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconHandle from '@/components/icon/IconHandle.vue'
import IconEye from '@/components/icon/IconEye.vue'
import IconEyeCrossed from '@/components/icon/IconEyeCrossed.vue'
import IconPlayerVolumeOn from '@/components/icon/IconPlayerVolumeOn.vue'
import IconPlayerVolumeOff from '@/components/icon/IconPlayerVolumeOff.vue'
import MediaflowMenu from './MediaflowMenu.vue'
import { mediaTypeMeta, mediaHasAudio, DOCK_DANGER_ITEM } from './mediaflowMeta'

/**
 * One source in the Sources dock, OBS style: eye, type, name, then inline
 * mute + volume. Audio sync opens under the row from the kebab. Presentation
 * only — every change is emitted and the parent calls the API.
 */
const props = defineProps({
	media: { type: Object, required: true },
	index: { type: Number, required: true },
	count: { type: Number, required: true },
	enabled: { type: Boolean, default: true },
	previewAvailable: { type: Boolean, default: false },
	/** Secondary text, e.g. the linked stream or file name. */
	subtitle: { type: String, default: '' },
	/** Selected on the canvas. */
	selected: { type: Boolean, default: false },
	/** 'new' (staged add) | 'changed' (staged edit) | null. */
	draft: { type: String, default: null },
	/** The scene is on Program: say that audio is not drafted. */
	audioLive: { type: Boolean, default: false },
	/** No Edit / Remove (the pop-out leaves those to the main window). */
	readOnly: { type: Boolean, default: false },
})

const emit = defineEmits([
	'toggle-show', 'toggle-mute', 'volume', 'audio-sync', 'preview', 'edit', 'delete', 'select', 'move', 'position',
])

const syncOpen = ref(false)

// Clicks on controls must not change the canvas selection.
const INTERACTIVE = 'button, input, label, a, [role=dialog], .dropdown-wrapper'
const onRowClick = (event) => {
	if (event.target.closest(INTERACTIVE)) return
	emit('select')
}
const onRowKey = (event) => {
	if (event.target !== event.currentTarget) return
	if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); emit('select') }
}

const meta = computed(() => mediaTypeMeta(props.media.type))
const hasAudio = computed(() => mediaHasAudio(props.media))
const volume = computed(() => {
	const v = parseInt(props.media.volume, 10)
	return Number.isNaN(v) ? 100 : Math.max(0, Math.min(100, v))
})
const muted = computed(() => !!props.media.muted)
// A staged source does not exist on the server yet: no mixer until it is applied.
const audioEnabled = computed(() => props.enabled && props.draft !== 'new')
const audioHint = computed(() => {
	if (props.draft === 'new') return 'Apply the draft first to adjust its audio'
	return props.audioLive ? 'Audio changes apply live' : ''
})
const draftHint = computed(() => (props.draft === 'new' ? 'Added in Preview. Not live until you apply.' : 'Changed in Preview. Not live until you apply.'))
const visible = computed(() => props.media.show !== false)

const onVolumeInput = (event) => {
	const v = parseInt(event.target.value, 10)
	emit('volume', Number.isNaN(v) ? 50 : Math.max(0, Math.min(100, v)))
}
const onSync = (value) => {
	const offset = parseInt(value, 10)
	emit('audio-sync', Number.isNaN(offset) ? 0 : Math.max(0, Math.min(1000, offset)))
}
</script>

<template>
	<li
		class="mf-row source-row rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-main"
		:class="selected ? 'bg-main-transparent ring-1 ring-main' : ''"
		:data-tone="selected ? 'selected' : undefined"
		:data-index="index"
		:data-selected="selected ? 'true' : undefined"
		:data-hidden="!visible ? 'true' : undefined"
		:data-draft="draft || undefined"
		data-testid="source-row"
		tabindex="0"
		:aria-label="`${media.name}, ${meta.label}${!visible ? ', hidden' : ''}`"
		:aria-current="selected ? 'true' : undefined"
		@click="onRowClick"
		@keydown="onRowKey"
	>
		<div class="flex h-9 items-center gap-0.5 px-0.5">
			<span
				class="source-drag-handle flex w-4 shrink-0 items-center justify-center text-surface-6"
				:class="enabled ? 'cursor-grab hover:text-surface-text-1 active:cursor-grabbing' : 'opacity-40'"
				title="Drag to reorder"
				aria-hidden="true"
			>
				<icon-base width="12" height="12"><icon-handle /></icon-base>
			</span>
			<button
				type="button"
				class="mf-icon-btn"
				:class="visible ? 'text-surface-8' : 'text-surface-6'"
				:disabled="!enabled"
				:aria-pressed="visible"
				:aria-label="visible ? `Hide ${media.name}` : `Show ${media.name}`"
				:title="visible ? 'Hide' : 'Show'"
				data-testid="source-visibility"
				@click="emit('toggle-show')"
			>
				<icon-base width="14" height="14" fill="none">
					<icon-eye v-if="visible" />
					<icon-eye-crossed v-else />
				</icon-base>
			</button>
			<span class="flex size-6 shrink-0 items-center justify-center rounded-md" :class="meta.tone" :title="meta.label">
				<icon-base width="12" height="12" :width-view-box="meta.viewBox" :height-view-box="meta.viewBox" :fill="meta.fill"><component :is="meta.icon" /></icon-base>
			</span>
			<span class="ml-1.5 flex min-w-0 flex-1 flex-col leading-tight">
				<span class="flex min-w-0 items-center gap-1.5">
					<span class="truncate text-[0.8rem] font-medium" :class="visible ? 'text-surface-text-2' : 'text-surface-7 line-through decoration-surface-6'" :title="subtitle ? `${media.name} · ${subtitle}` : media.name">{{ media.name }}</span>
					<span v-if="draft" class="size-1.5 shrink-0 rounded-full bg-gold" :title="draftHint" data-testid="source-draft-dot" />
					<span v-if="draft" class="sr-only">{{ draftHint }}</span>
				</span>
			</span>
			<Tooltip v-if="hasAudio" :content="audioHint" wrapper-class="inline-flex shrink-0 items-center" popper-class="max-w-[220px] text-sm break-words">
				<button
					type="button"
					class="mf-icon-btn"
					:class="muted ? 'text-error-light' : 'text-surface-8'"
					:disabled="!audioEnabled"
					:aria-pressed="muted"
					:aria-label="muted ? `Unmute ${media.name}` : `Mute ${media.name}`"
					:title="muted ? 'Unmute' : 'Mute'"
					data-testid="source-mute"
					@click="emit('toggle-mute')"
				>
					<icon-base width="14" height="14" :width-view-box="20" :height-view-box="20" fill="none">
						<icon-player-volume-off v-if="muted" />
						<icon-player-volume-on v-else />
					</icon-base>
				</button>
				<input
					type="range"
					class="volume-slider hidden w-12 shrink-0 2xl:block"
					:class="muted ? 'opacity-40' : ''"
					min="0"
					max="100"
					step="1"
					:value="volume"
					:disabled="!audioEnabled"
					:aria-label="`Volume for ${media.name}`"
					:aria-valuetext="`${volume}%`"
					:title="`Volume ${volume}%`"
					data-testid="source-volume"
					@input="onVolumeInput"
				/>
			</Tooltip>
			<MediaflowMenu :aria-label="`Actions for ${media.name}`" list-classes="min-w-[200px]">
				<DropDownMenuItem v-if="!readOnly" :disabled="!enabled" @click="emit('edit')">Edit source…</DropDownMenuItem>
				<DropDownMenuItem :disabled="!enabled" @click="emit('position')">Position &amp; size</DropDownMenuItem>
				<DropDownMenuItem v-if="hasAudio" @click="syncOpen = !syncOpen">{{ syncOpen ? 'Hide audio' : 'Volume & sync…' }}</DropDownMenuItem>
				<DropDownMenuItem v-if="previewAvailable" @click="emit('preview')">Preview source</DropDownMenuItem>
				<DropDownMenuItem :disabled="!enabled || index === 0" @click="emit('move', -1)">Move up (to front)</DropDownMenuItem>
				<DropDownMenuItem :disabled="!enabled || index === count - 1" @click="emit('move', 1)">Move down (to back)</DropDownMenuItem>
				<DropDownMenuItem v-if="!readOnly" :disabled="!enabled" :custom-classes="DOCK_DANGER_ITEM" @click="emit('delete')">Remove from scene</DropDownMenuItem>
			</MediaflowMenu>
		</div>

		<!-- Volume (narrow screens) + audio sync -->
		<div v-if="hasAudio && syncOpen" class="flex flex-col gap-2 px-2.5 pb-2.5 pt-1" data-testid="audio-strip">
			<p v-if="audioHint" class="m-0 text-[0.7rem] text-surface-7" data-testid="audio-live-hint">{{ audioHint }}.</p>
			<label class="flex min-w-0 items-center gap-2 text-xs text-surface-8">
				<span class="w-12 shrink-0">Volume</span>
				<input
					type="range"
					class="volume-slider w-full min-w-[80px]"
					:class="muted ? 'opacity-40' : ''"
					min="0"
					max="100"
					step="1"
					:value="volume"
					:disabled="!audioEnabled"
					:aria-label="`Volume for ${media.name}`"
					@input="onVolumeInput"
				/>
				<span class="w-9 shrink-0 text-right tabular-nums text-surface-text-1">{{ volume }}%</span>
			</label>
			<label class="flex items-center gap-2 text-xs text-surface-8" title="Delay audio to match video (0–1000 ms)">
					<span class="w-12 shrink-0">Sync</span>
					<input
						type="number"
						min="0"
						max="1000"
						class="mf-input w-[68px] tabular-nums"
						:value="media.audioOffset ?? 0"
						:disabled="!audioEnabled"
						aria-label="Audio sync offset in milliseconds"
						data-testid="source-sync"
						@input="onSync($event.target.value)"
					>
					<span>ms</span>
				</label>
		</div>
	</li>
</template>

<style scoped>
.mf-icon-btn {
	display: flex;
	width: 1.75rem;
	height: 1.75rem;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	border-radius: 0.375rem;
	transition: color 0.15s ease, background-color 0.15s ease;
	outline: none;
}
.mf-icon-btn:hover:not(:disabled) {
	color: #fff;
	background-color: color-mix(in srgb, var(--surface-4) 60%, transparent);
}
.mf-icon-btn:focus-visible {
	box-shadow: 0 0 0 2px var(--c-main);
}
.mf-icon-btn:disabled {
	cursor: not-allowed;
	opacity: 0.5;
}
.volume-slider {
	-webkit-appearance: none;
	appearance: none;
	height: 3px;
	border-radius: 9999px;
	background: var(--surface-4);
	outline: none;
	cursor: pointer;
}
.volume-slider:disabled {
	cursor: not-allowed;
	opacity: 0.5;
}
.volume-slider::-webkit-slider-thumb {
	-webkit-appearance: none;
	appearance: none;
	width: 10px;
	height: 10px;
	border-radius: 9999px;
	background: #fff;
	border: 0;
	box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25);
	transition: transform 0.15s ease;
}
.volume-slider:hover::-webkit-slider-thumb {
	transform: scale(1.15);
}
.volume-slider::-moz-range-thumb {
	width: 10px;
	height: 10px;
	border-radius: 9999px;
	background: #fff;
	border: 0;
}
.volume-slider:focus-visible {
	box-shadow: 0 0 0 3px var(--c-main-transparent);
}
</style>
